import { ProcessingStatus } from '@/types/Document';

interface DocumentActionsProps {
  documentId: string;
  processingStatus: ProcessingStatus | undefined;
  isProcessing: boolean;
  onProcess: (documentId: string) => void;
  onDownload: (documentId: string, filename: string) => void;
  onDelete: (documentId: string) => void;
  originalName: string;
}

export default function DocumentActions({
  documentId,
  processingStatus,
  isProcessing,
  onProcess,
  onDownload,
  onDelete,
  originalName
}: DocumentActionsProps) {
  const isInProgress = isProcessing || 
    processingStatus === 'queued' || 
    processingStatus === 'processing' || 
    processingStatus === 'uploading' || 
    processingStatus === 'in_progress' ||
    processingStatus === 'preparing';

  const getProcessButtonStyle = () => {
    if (isInProgress) {
      return 'bg-blue-100 text-blue-800 border border-blue-300';
    }
    if (processingStatus === 'done') {
      return 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200';
    }
    if (processingStatus === 'error') {
      return 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100';
    }
    return 'bg-green-100 text-green-800 border border-green-300 hover:bg-green-200';
  };

  const getProcessButtonContent = () => {
    if (isInProgress) {
      return (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          In Progress
        </>
      );
    }
    if (processingStatus === 'done') {
      return (
        <>
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reprocess
        </>
      );
    }
    if (processingStatus === 'error') {
      return (
        <>
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Retry
        </>
      );
    }
    return (
      <>
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        Process
      </>
    );
  };

  return (
    <div className="flex items-center space-x-3">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onProcess(documentId);
        }}
        className={`flex items-center justify-center min-w-[140px] px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 ${getProcessButtonStyle()}`}
        disabled={isInProgress}
      >
        {getProcessButtonContent()}
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDownload(documentId, originalName);
        }}
        className="text-blue-600 hover:text-blue-900 p-1.5 rounded-md hover:bg-blue-50"
        title="Download"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
        </svg>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(documentId);
        }}
        className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50"
        title="Delete"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
        </svg>
      </button>
    </div>
  );
}
